import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import dayjs from "dayjs";

import { MeetingDefinition } from "src/types";
import MeetingTimePicker from "./MeetingTimePicker";
import CalendarInvite from "./CalendarInvite";
import WebinarIntegrationModal from "./dialogs/WebinarIntegrationModal";
import EventTypeSelect from "./EventTypeSelect";
import WebinarDurationSlider from "./WebinarDurationSlider";
import useTimezones from "../components/timezone/useTimezones";

function WebinarEditTimeAttributesAndInvite() {
  const { control, setValue, watch } = useFormContext<MeetingDefinition>();
  const [integrationModalOpen, setIntegrationModalOpen] =
    useState<boolean>(false);

  const webinarStartTime = watch("webinarConfig.startTime");
  const { getTimezoneAbbreviation, getBrowserTimezoneInUTC } = useTimezones();

  // Calculate user timezone and display string
  const userTimezone = getBrowserTimezoneInUTC() || "UTC";

  const timezoneDisplay = useMemo(() => {
    const offsetMin = dayjs().tz(userTimezone).utcOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    const hh = String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, "0");
    const mm = String(Math.abs(offsetMin) % 60).padStart(2, "0");

    return `${getTimezoneAbbreviation(userTimezone)} (UTC${sign}${hh}:${mm})`;
  }, [userTimezone, getTimezoneAbbreviation]);

  // Set default date and time if not set or if it's the invalid date (12/31/0000)
  useEffect(() => {
    const currentTime = webinarStartTime ? dayjs(webinarStartTime) : null;

    // Reset time if it's invalid, not set, or in the past
    if (
      currentTime &&
      currentTime.startOf("day").isBefore(dayjs().startOf("day"))
    ) {
      const defaultTime = dayjs().hour(12).minute(0).second(0).millisecond(0);
      // Only set if the value is actually different
      if (
        !currentTime ||
        currentTime.startOf("day").isBefore(dayjs().startOf("day"))
      ) {
        setValue("webinarConfig.startTime", defaultTime, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }
  }, [webinarStartTime]); // Remove setValue from dependencies to prevent loops

  return (
    <>
      <WebinarIntegrationModal
        open={integrationModalOpen}
        onClose={() => {
          setIntegrationModalOpen(false);
        }}
        onSubmit={() => {
          console.info("submit");
        }}
      />
      <Stack direction="row" width="100%">
        {/* left side */}
        <Stack direction="column" width="33%">
          <Box>
            <Box sx={{ mb: 5 }}>
              <Controller
                control={control}
                name="webinarConfig.meetingType"
                render={({ field }) => (
                  <EventTypeSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Box>

            {/* DISABLED FOR V1 */}
            {/*
            <Box sx={{ mb: 5 }}>
              <Typography component="span">
                To import parameters for an existing webinar from your webinar
                platform, click{" "}
                <Typography
                  onClick={() => {
                    setIntegrationModalOpen(true);
                  }}
                  component="span"
                  sx={{
                    color: "#2BAEF9",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  here
                </Typography>{" "}
                (optional)
              </Typography>
            </Box> */}

            <Box sx={{ mb: 6 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  Event Start Time
                </Typography>
                <Typography>What time will your event start?</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  The start time is in {timezoneDisplay} timezone.
                </Typography>
              </Box>
              <Controller
                control={control}
                name="webinarConfig.startTime"
                render={({ field }) => (
                  <MeetingTimePicker
                    value={field.value || null}
                    onChange={field.onChange}
                  />
                )}
              />
            </Box>

            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  Event Duration
                </Typography>
                <Typography>How long should the event last for?</Typography>
              </Box>
              <Controller
                control={control}
                name="properties.duration"
                render={({ field }) => (
                  <>
                    <WebinarDurationSlider
                      valueMinutes={field.value}
                      onChangeMinutes={field.onChange}
                      ariaLabel="Event Duration"
                    />
                  </>
                )}
              />
            </Box>
          </Box>
        </Stack>

        <Box sx={{ m: 2 }} />

        {/* right side */}
        <Stack direction="column" width="67%">
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" fontWeight="bold">
              Invite Content
            </Typography>
            <Typography>
              Decide what attendees will see in the calendar event for this
              event
            </Typography>
          </Box>

          <CalendarInvite />
        </Stack>
      </Stack>
    </>
  );
}

export default WebinarEditTimeAttributesAndInvite;
